import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, LogIn, Copy, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { homeFor } from "../ProtectedRoute";

const gen = () => "Gs-" + Math.random().toString(36).slice(2, 7) + Math.random().toString(36).slice(2, 6).toUpperCase() + "!";

export function SetPasswordDialog({ user, onClose, onDone }) {
  const [pwd, setPwd] = useState(gen());
  const [sendMail, setSendMail] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const save = async () => {
    if (pwd.length < 8) { toast.error("Minimo 8 caratteri"); return; }
    setSaving(true);
    try { const { data } = await api.post(`/registrations/${user.id}/set-password`, { password: pwd, send_email: sendMail }); setResult(data); onDone?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const copy = () => { navigator.clipboard?.writeText(`${user.email} / ${result.password}`); toast.success("Copiato"); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="set-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-[#0B2A45] flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#C2593F]" />Imposta password</DialogTitle>
          <DialogDescription>{user.name} · {user.email}</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3" data-testid="set-password-result">
            <div className="rounded-xl bg-[#FEF9C3] border border-[#FDE68A] p-4 text-sm">
              <div className="text-xs uppercase tracking-wider text-[#854D0E] mb-1">Credenziali (mostrate una sola volta)</div>
              <div><b>Email:</b> {user.email}</div>
              <div><b>Password:</b> <span className="font-mono text-base" data-testid="set-password-value">{result.password}</span></div>
              <div className="text-xs text-stone-500 mt-2">{result.emailed ? "Inviata via email all'utente." : "Email non inviata."}</div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={copy} className="rounded-full flex-1" data-testid="set-password-copy"><Copy className="w-4 h-4 mr-1" />Copia</Button>
              <Button onClick={onClose} className="rounded-full flex-1 bg-[#0F4C81]" data-testid="set-password-close">Chiudi</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input value={pwd} onChange={(e) => setPwd(e.target.value)} className="font-mono" data-testid="set-password-input" />
              <Button type="button" variant="outline" onClick={() => setPwd(gen())} className="rounded-full" data-testid="set-password-generate" title="Genera"><RefreshCw className="w-4 h-4" /></Button>
            </div>
            <label className="flex items-center gap-2 text-sm text-stone-600"><input type="checkbox" checked={sendMail} onChange={(e) => setSendMail(e.target.checked)} data-testid="set-password-sendmail" />Invia la password via email all'utente</label>
            <p className="text-xs text-stone-400">La password viene salvata e le sessioni attive dell'utente vengono chiuse.</p>
            <Button onClick={save} disabled={saving} className="w-full rounded-full bg-[#0F4C81] h-11" data-testid="set-password-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva password"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ImpersonateButton({ user, className = "", compact = false }) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  if (user.status !== "active") return null;
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/impersonate/start/${user.id}`); setUser(data); toast.success(`Stai operando come ${data.name}`); navigate(homeFor(data.role)); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" onClick={go} disabled={busy} className={`rounded-full h-8 text-xs border-[#0F4C81]/30 text-[#0F4C81] hover:bg-[#EFF6FF] ${className}`} data-testid={`impersonate-${user.id}`}>
      <LogIn className="w-3.5 h-3.5 mr-1" />{compact ? "Accedi come" : "Accedi come utente"}
    </Button>
  );
}
